/** Parse an ISO date-only string as midday UTC so it never slips a day by zone. */
function parse(iso: string): Date {
  return new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso)
}

/* ─────────────────────────────────────────────────────────────────────────────
   Three ways to say a date, and the rule for picking.

   There were seven, several of them on one screen: `2026-10-02` in the Clients
   table, `10/02/2026` with a native calendar glyph on every board row, the literal
   `mm/dd/yyyy` on every undated one, `Sep 16` on Overview, `Sep 16, 2026` on the
   Activity day heads, `Mar 25, 2026` in the aside, and the relative forms. The
   first three came off the native date input and went with it (see
   components/Picker.tsx). These are the three that are left:

     ISO          `2026-10-02`, in mono — anything in a COLUMN, and anything a
                  machine set. It is the house style and it wins wherever data is
                  tabulated. There is no helper for it: the stored value already
                  is one, which is half the point.
     spoken       `2 Oct 2026` — where somebody is reading a sentence rather than
                  scanning a column: the client's page, an activity day head.
     relative     `2d ago`, `in 14d` — beside an absolute one wherever there is
                  room, and carrying it in a `title` where there is not, because
                  "4mo ago" on its own cannot be checked against anything.

   **Spoken is spelled out rather than localised.** These used to be
   `toLocaleDateString(undefined, …)`, which is why the app said `Sep 16, 2026` on
   one machine and `16 Sept 2026` on another — two of the seven forms were the same
   call answering differently. A client's page should read the same to the client
   and to the team member who sent it.
   ───────────────────────────────────────────────────────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2 Oct 2026" — the spoken form. */
export function formatDate(iso: string): string {
  const d = parse(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** "2 Oct" — the same, without the year, for a timeline or a metadata row. */
export function formatShortDate(iso: string): string {
  const d = parse(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

/** "2 Oct, 14:05" — spoken, plus the time, for a stamp on a single event. */
export function formatDateTime(iso: string): string {
  const d = parse(iso)
  if (Number.isNaN(d.getTime())) return ''
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${formatShortDate(iso)}, ${hh}:${mm}`
}

/** The absolute date behind a relative stamp, for a `title`. A column has no room
 *  for both, and a relative time nobody can resolve is a number without a unit. */
export function absoluteOf(iso: string): string {
  return iso.length > 10 ? formatDateTime(iso) : formatDate(iso)
}

/**
 * Always a relative form, never falling back to an absolute date — for places
 * that already show the date on an adjacent line, where the fallback would just
 * print it twice.
 */
export function relativeShort(iso: string): string {
  const d = parse(iso).getTime()
  if (Number.isNaN(d)) return ''
  const days = Math.round((Date.now() - d) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 31) return `${Math.round(days / 7)}w ago`
  if (days < 365) return `${Math.round(days / 30)}mo ago`
  return `${Math.round(days / 365)}y ago`
}

export function relativeTime(iso: string): string {
  const d = parse(iso).getTime()
  if (Number.isNaN(d)) return ''
  const secs = Math.round((Date.now() - d) / 1000)
  if (secs < 45) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.round(days / 7)}w ago`
  return formatDate(iso)
}

/** How far off a due date is. The mirror of relativeShort, which only looks back —
 *  a deadline needs "in 4d" and "3d overdue", which that one cannot say. */
export function dueIn(iso: string): string {
  const d = parse(iso).getTime()
  if (Number.isNaN(d)) return ''
  const days = Math.round((d - new Date(`${todayIso()}T00:00:00`).getTime()) / 86_400_000)
  if (days < 0) return `${-days}d overdue`
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days < 31) return `in ${days}d`
  return `in ${Math.round(days / 7)}w`
}

/** Today as a yyyy-mm-dd string, in local time (what a date input expects). */
export function todayIso(): string {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export function formatMoney(value: number): string {
  if (!Number.isFinite(value)) return ''
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

/** "$12,000", or an em-dash-free placeholder when there's no number yet. */
export function money(value: number, empty = '·'): string {
  return value > 0 ? `$${formatMoney(value)}` : empty
}

/** Compact money for tight spots: $48k. */
export function moneyShort(value: number, empty = '·'): string {
  if (!(value > 0)) return empty
  if (value >= 1000) return `$${Math.round(value / 1000)}k`
  return `$${formatMoney(value)}`
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/**
 * Bucket a set of timestamps into the last `weeks` weekly counts, oldest first —
 * the input to a contact sparkline. Derived from the log, stored nowhere.
 */
export function weeklyCounts(isoDates: string[], weeks = 12, now = Date.now()): number[] {
  const out = new Array(weeks).fill(0) as number[]
  const week = 7 * 86_400_000
  for (const iso of isoDates) {
    const t = parse(iso).getTime()
    if (Number.isNaN(t)) continue
    const idx = weeks - 1 - Math.floor((now - t) / week)
    if (idx >= 0 && idx < weeks) out[idx] = (out[idx] ?? 0) + 1
  }
  return out
}

/** A URL as you'd want it in a table cell: no protocol, no www, no trailing slash. */
export function hostPath(url: string, max = 28): string {
  const bare = url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
  return bare.length > max ? bare.slice(0, max - 1) + '…' : bare
}
